import { createMemo, Show } from "solid-js";
import { formatRelative, useRelativeTimeTick } from "../services/relativeTime";
import { formatDateTime, formatDateTimeShort, formatTime } from "../services/dateFormat";
import { useTranslation } from "../hooks/useTranslation";

interface RelativeTimeProps {
  value: Date | string | number | null | undefined;
  fallback?: string;
  showAbsolute?: boolean;
}

export function RelativeTime(props: RelativeTimeProps) {
  const tick = useRelativeTimeTick();
  const { t } = useTranslation();
  const labels = createMemo(() => ({
    justNow: t("RELATIVE_TIME_JUST_NOW"),
    past: (v: string) => t("RELATIVE_TIME_PAST", v),
    future: (v: string) => t("RELATIVE_TIME_FUTURE", v),
  }));
  const date = createMemo(() => {
    if (props.value == null) return null;
    const d = props.value instanceof Date ? props.value : new Date(props.value);
    return Number.isNaN(d.getTime()) ? null : d;
  });
  const label = createMemo(() => {
    const d = date();
    if (!d) return props.fallback ?? "";
    return formatRelative(d, tick(), labels());
  });
  const absolute = createMemo(() => {
    const d = date();
    if (!d) return "";
    const now = new Date(tick());
    const sameDay = d.getFullYear() === now.getFullYear()
      && d.getMonth() === now.getMonth()
      && d.getDate() === now.getDate();
    return sameDay ? formatTime(d) : formatDateTimeShort(d);
  });
  return (
    <time title={date() ? formatDateTime(date()) : undefined} dateTime={date()?.toISOString()}>
      {label()}
      <Show when={props.showAbsolute && absolute()}>
        {" · "}{absolute()}
      </Show>
    </time>
  );
}
